import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  useRemoveRFIDTagMutation,
  useViewRFIDListQuery,
} from "../../../redux/api/ev-charger/evChargerAPI";
import Reveal from "../../../shared/components/reveal/reveal";
import useDebounce from "../../../shared/hooks/useDebounce";
import OcppTagResponseDTO from "../../../shared/oversight-core/dtos/response-dtos/ocpp-tag-response-dto";
import { IHttpError } from "../../../shared/oversight-core/interfaces/http-errror";
import AppDropDown from "../../../shared/oversight-core/ui-elements/app-dropdown/app-drop-down";
import AppButton from "../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import DataTable from "../../../shared/oversight-core/ui-elements/data-table/data-table";
import Search from "../../../shared/oversight-core/ui-elements/search/search";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import AddUpdateRfidModal from "./components/add-update-rfid-modal/add-update-rfid-modal";
import AssignUserModal from "./components/assign-user-modal/assign-user-modal";
import RemoveRfidModal from "./components/remove-rfid-modal/remove-rfid-modal";
import RfidUserCard from "./components/rfid-user-card/rfid-user-card";
import styles from "./users.module.scss";

const MIN_AUTO_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 500;

const Users = () => {
  const [searchText, setSearchText] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_MS);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showAddRfidModal, setShowAddRfidModal] = useState(false);
  const [showRemoveRfidModal, setShowRemoveRfidModal] = useState(false);
  const [selectedRfid, setSelectedRfid] = useState<OcppTagResponseDTO | null>(
    null,
  );
  const [selectedRfidForRemove, setSelectedRfidForRemove] =
    useState<OcppTagResponseDTO | null>(null);
  const [selectedRfidForAssign, setSelectedRfidForAssign] =
    useState<OcppTagResponseDTO | null>(null);

  const { data: rfidList = [], isFetching } = useViewRFIDListQuery({
    search: activeSearch,
  });

  const [removeRFIDTag, { isLoading: isRemovingRfid }] =
    useRemoveRFIDTagMutation();

  const handleSearchChange = (val: string) => {
    setSearchText(val);
    if (!val.trim()) {
      setActiveSearch("");
    }
  };

  // auto-search once the user has typed enough to narrow the list; shorter
  // queries can still be submitted explicitly with Enter or the search button
  useEffect(() => {
    const trimmed = debouncedSearchText.trim();
    if (trimmed.length >= MIN_AUTO_SEARCH_LENGTH) {
      setActiveSearch(trimmed);
    }
  }, [debouncedSearchText]);

  const handleSearch = () => {
    setActiveSearch(searchText.trim());
  };

  const handleOpenAddModal = () => {
    setSelectedRfid(null);
    setShowAddRfidModal(true);
  };

  const handleOpenEditModal = (item: OcppTagResponseDTO) => {
    setSelectedRfid(item);
    setShowAddRfidModal(true);
  };

  const handleCloseRfidModal = () => {
    setShowAddRfidModal(false);
    setSelectedRfid(null);
  };

  const handleOpenRemoveModal = (item: OcppTagResponseDTO) => {
    setSelectedRfidForRemove(item);
    setShowRemoveRfidModal(true);
  };

  const handleCloseRemoveModal = () => {
    setShowRemoveRfidModal(false);
    setSelectedRfidForRemove(null);
  };

  const handleOpenAssignUserModal = (item: OcppTagResponseDTO) => {
    setSelectedRfidForAssign(item);
    setShowAssignUserModal(true);
  };

  const handleCloseAssignUserModal = () => {
    setShowAssignUserModal(false);
    setSelectedRfidForAssign(null);
  };

  const handleConfirmRemove = () => {
    if (!selectedRfidForRemove?.ocppTagId) return;

    removeRFIDTag({ ocppTagId: selectedRfidForRemove.ocppTagId })
      .unwrap()
      .then(() => {
        showSuccessMessage("RFID Tag removed successfully");
        handleCloseRemoveModal();
      })
      .catch((err: IHttpError) => {
        showErrorMessage(err.message || "Failed to remove RFID Tag");
      });
  };

  return (
    <div className="position-relative">
      <Reveal>
        <Row className="align-items-center g-4 mb-3">
          <Col className="col col-sm-7 col-md-6 col-lg-5 col-xl-4 col-xxl-3">
            <Search
              placeholder="Search User"
              value={searchText}
              handleSearchChange={handleSearchChange}
              onSearch={handleSearch}
            />
          </Col>
          <Col className="col-auto">
            <Row className="d-none d-sm-block">
              <Col>
                <AppButton
                  text="Add RFID"
                  type="button"
                  variant="secondary"
                  onClick={handleOpenAddModal}
                />
              </Col>
            </Row>
            <Row className="d-block d-sm-none">
              <Col>
                <AppButton
                  iconOnly
                  iconName="add"
                  text="Add RFID"
                  type="button"
                  variant="secondary"
                  onClick={handleOpenAddModal}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Reveal>

      {rfidList.length > 0 ? (
        <>
          <Row className="mt-3 d-none d-md-flex">
            <Col xs={12}>
              <Reveal delay={80}>
                <div className="table-responsive">
                  <DataTable
                    headers={[
                      {
                        id: 1,
                        columnName: "RFID",
                        isSort: true,
                      },
                      {
                        id: 2,
                        columnName: "Name",
                      },
                      {
                        id: 3,
                        columnName: "Email",
                      },
                      {
                        id: 4,
                        columnName: "Note",
                      },
                      {
                        id: 5,
                        columnName: "",
                      },
                    ]}
                    data={(rfidList || []).map((item, index) => ({
                      data: [
                        <span
                          key={`rfid-${index}`}
                          className="font-weight-400 font-size-14 text-dark"
                        >
                          {item.idTag || "-"}
                        </span>,
                        <span
                          key={`name-${index}`}
                          className="font-weight-400 font-size-14 text-dark"
                        >
                          {item.holderName || "-"}
                        </span>,
                        <span
                          key={`email-${index}`}
                          className="font-weight-400 font-size-14 text-dark"
                        >
                          {item.email || "-"}
                        </span>,
                        <span
                          key={`note-${index}`}
                          className="font-weight-400 font-size-14 text-dark"
                        >
                          {item.note || "-"}
                        </span>,
                        <span
                          key={`action-${index}`}
                          className="d-flex justify-content-end"
                        >
                          <AppDropDown
                            items={[
                              {
                                text: "Edit",
                                onClick: () => handleOpenEditModal(item),
                              },
                              {
                                text: "Remove",
                                onClick: () => handleOpenRemoveModal(item),
                              },
                              {
                                text: "Assign User",
                                onClick: () => handleOpenAssignUserModal(item),
                              },
                            ]}
                            hasDropdownBg={false}
                          />
                        </span>,
                      ],
                    }))}
                    className={styles.usersTable}
                  />
                </div>
              </Reveal>
            </Col>
          </Row>
          <div className="d-md-none mt-3">
            {rfidList.map((item, index) => (
              <Reveal
                key={item.ocppTagId}
                delay={Math.min(index, 8) * 50}
                className="mb-3"
              >
                <RfidUserCard
                  item={item}
                  onEdit={handleOpenEditModal}
                  onRemove={handleOpenRemoveModal}
                  onAssignUser={handleOpenAssignUserModal}
                />
              </Reveal>
            ))}
          </div>
        </>
      ) : (
        <Row className="container-dash mx-0 text-light justify-content-center mt-4">
          <Col className="col-auto">There no users list</Col>
        </Row>
      )}
      <AssignUserModal
        show={showAssignUserModal}
        tagData={selectedRfidForAssign}
        onClose={handleCloseAssignUserModal}
        onCancel={handleCloseAssignUserModal}
      />
      <AddUpdateRfidModal
        show={showAddRfidModal}
        tagData={selectedRfid}
        onClose={handleCloseRfidModal}
        onCancel={handleCloseRfidModal}
      />
      <RemoveRfidModal
        show={showRemoveRfidModal}
        idTag={selectedRfidForRemove?.idTag}
        isLoading={isRemovingRfid}
        onClose={handleCloseRemoveModal}
        onCancel={handleCloseRemoveModal}
        onConfirm={handleConfirmRemove}
      />
      <SpinnerModal show={isFetching} />
    </div>
  );
};

export default Users;
